<template>
  <div class="translate-wrapper">
    <ValidationObserver
      ref="validationObserver"
      v-slot="{ handleSubmit }"
    >
      <Loader :isVisible="isLoading" />
      <form
        class="contact-info"
        novalidate="novalidate"
        @submit.prevent="handleSubmit(submit)"
        @input="handleFormChange"
      >
        <MaterialInput
          v-model="formData.title"
          v-bind="formFields.title"
          :label="'Title'"
        />
        <MaterialInput
          v-model="formData.shortDescription"
          v-bind="formFields.shortDescription"
          :label="'Short Description'"
        />
        <MaterialTextArea
          v-model="formData.longDescription"
          v-bind="formFields.longDescription"
          :label="'Long Description'"
        />
        <div class="btn-wrapper">
          <div class="btn-wrapper-b">
            <Button
              title="Revert"
              classType="warning"
              :eventFunction="revertFolderLanguage"
            />
            <Button
              type="submit"
              title="Apply"
              classType="primary"
            />
          </div>
          <div
            v-if="isEditedForm.length
              && isEditedForm.find(item => item.id === lang.id && !!item.value)"
            class="changes-wrapper"
          >
            <inline-svg
              :src="iconChanges"
              class="svg-icon"
            >
            </inline-svg>
            <span class="changes-text">
              Unsaved changes
            </span>
          </div>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import BaseForm from '../BaseForm.vue';
import MaterialInput from '../../inputs/MaterialInput.vue';
import MaterialTextArea from '../../inputs/MaterialTextArea.vue';
import Button from '../../common/Button.vue';
import iconChanges from '../../../assets/icons/icon-localization-changes.svg';

import {
  ADD_LANGUAGE_FOLDER_REQUEST,
  GET_LANGUAGE_FOLDER_REQUEST,
  INIT_FORM_CHANGES,
  SET_FORM_CHANGES,
} from '../../../store/actions/localization/localizationActions';
import Loader from '../../common/Loader.vue';

export default {
  name: 'CollectionGeneralTranslateForm',
  components: {
    Loader,
    MaterialInput,
    MaterialTextArea,
    Button,
  },
  extends: BaseForm,
  props: {
    metadata: {
      type: Object,
      default: () => {},
    },
    lang: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enabledLanguages: {
      type: Array,
      default: () => [],
    },
    changedLanguage: {
      type: Function,
      default: () => {},
    },
    type: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      iconChanges,
      submitEvent: ADD_LANGUAGE_FOLDER_REQUEST,
      successMessages: `The ${this.lang.name} Localization has been successfully updated`,
      formData: {
        title: '',
        shortDescription: '',
        longDescription: '',
      },
      formFields: {
        title: {
          type: 'text',
          name: 'title',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 255,
          },
        },
        shortDescription: {
          type: 'text',
          name: 'shortDescription',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 255,
          },
        },
        longDescription: {
          type: 'text',
          name: 'longDescription',
          cols: 30,
          rows: 10,
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 1800,
          },
        },
      },
      errorMapping: {
        title: [
          `${this.formPrefix}.title`,
        ],
        shortDescription: [
          `${this.formPrefix}.shortDescription`,
        ],
        longDescription: [
          `${this.formPrefix}.longDescription`,
        ],
      },
    };
  },
  computed: {
    ...mapState({
      folderLanguage: (state) => state.localization.folderLanguage,
      isEditedForm: (state) => state.localization.isEditedForm,
      isLoading: (state) => state.localization.isLoading,
    }),
    ...mapGetters([
      'getCurrentAccountId',
    ]),
  },
  created() {
    const value = this.enabledLanguages.map(((item) => ({
      id: item.id,
      value: false,
    })));
    this.$store.dispatch(INIT_FORM_CHANGES, value);

    const params = {
      accountId: this.getCurrentAccountId,
      id: this.type === 'season' ? this.$route.params.seasonId : this.$route.params.id,
      type: this.type,
      langId: this.lang.id,
    };
    this.$store.dispatch(GET_LANGUAGE_FOLDER_REQUEST, params).then(() => {
      this.initFormData();
    });
  },
  beforeDestroy() {
    this.$store.dispatch(SET_FORM_CHANGES, []);
  },
  methods: {
    handleFormChange() {
      this.$emit('changedLanguage', { id: this.lang.id, value: true });
    },
    initFormData() {
      this.formData.title = this.folderLanguage.title;
      this.formData.shortDescription = this.folderLanguage.shortDescription;
      this.formData.longDescription = this.folderLanguage.longDescription;
    },
    revertFolderLanguage() {
      const params = {
        accountId: this.getCurrentAccountId,
        id: this.type === 'season' ? this.$route.params.seasonId : this.$route.params.id,
        type: this.type,
        langId: this.lang.id,
      };
      this.$store.dispatch(GET_LANGUAGE_FOLDER_REQUEST, params).then(() => {
        this.$emit('changedLanguage', { id: this.lang.id, value: false });
        this.$store.dispatch(SET_FORM_CHANGES, { id: this.lang.id, value: false });
        this.initFormData();
      });
    },
    getRequestData() {
      const data = {
        title: this.formData.title,
        shortDescription: this.formData.shortDescription,
        longDescription: this.formData.longDescription,
      };

      return {
        accountId: this.getCurrentAccountId,
        id: this.type === 'season' ? this.$route.params.seasonId : this.$route.params.id,
        type: this.type,
        langId: this.lang.id,
        data,
      };
    },
    onSubmitSuccess() {
      this.$emit('changedLanguage', { id: this.lang.id, value: false });
      this.$store.dispatch(SET_FORM_CHANGES, { id: this.lang.id, value: false });
      this.$toasted.global.success({
        message: this.successMessages,
      });
    },
    onSubmitFail(error) {
      this.formErrors = this.getServerFormErrors(error);

      if (Object.entries(this.formErrors).length && this.$refs[this.validationObserver]) {
        this.$refs[this.validationObserver].setErrors(this.formErrors);
      } else {
        this.$toasted.global.error({
          message: this.errorMessage,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.translate-wrapper {
  padding-left: 20px;
  padding-right: 428px;
}

.poster-title {
  color: #fff;
  font-weight: normal;
  margin-bottom: 10px;
}

.btn-wrapper {
  display: flex;
  padding-top: 35px;
}

.changes-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #FB3951;
  border-radius: 2px;
  padding: 8px;
  color: #FB3951;
  margin-left: 20px;
}

.changes-text {
  padding-left: 5px;
}

.btn-wrapper-b {
  display: flex;
}

@media all and (max-width: 1670px) {
  .translate-wrapper {
    padding-right: 300px;
  }
}

@media all and (max-width: 1460px) {
  .translate-wrapper {
    padding-right: 200px;
  }
}

@media all and (max-width: 1300px) {
  .translate-wrapper {
    padding-right: 100px;
  }
}
</style>
